import { c as _c } from "react/compiler-runtime";
import type { UserGiftListParams } from '@module/apis/client-side/heart/type';
import { itemsPerPage } from '@module/constants/pagenation';
import { useFetchUserGiftListQuery } from '@module/queries/heart/heartQueryHooks';
import useModalStore from '@module/stores/useModalStore';
import { MODAL_TYPE_PC } from '@module/types/modal/type';
import { convertDateToParamsFormat } from '@module/utils/date/convertDateFormat';
import getThreeMonthsAgo from '@module/utils/date/getThreeMonthsAgo';
import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { SendMessageModalType } from '../../../types/modal/message/type';
function useGiftListViewModel() {
  const $ = _c(42);
  const pushModal = useModalStore(_temp);
  const [searchType, setSearchType] = useState("each");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getThreeMonthsAgo();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [startDate, setStartDate] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [endDate, setEndDate] = useState(t1);
  const [searchId, setSearchId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const currentPageFirstOffset = (currentPage - 1) * itemsPerPage;
  let t2;
  if ($[2] !== startDate) {
    t2 = convertDateToParamsFormat(startDate);
    $[2] = startDate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== endDate) {
    t3 = convertDateToParamsFormat(endDate);
    $[4] = endDate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== currentPageFirstOffset || $[7] !== searchId || $[8] !== searchType || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      offset: currentPageFirstOffset,
      searchValue: searchId,
      limit: 10,
      type: searchType,
      startDate: t2,
      endDate: t3
    };
    $[6] = currentPageFirstOffset;
    $[7] = searchId;
    $[8] = searchType;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const [searchParams, setSearchParams] = useState(t4);
  let t5;
  if ($[12] !== currentPageFirstOffset || $[13] !== endDate || $[14] !== searchType || $[15] !== startDate) {
    t5 = () => {
      setSearchParams({
        offset: currentPageFirstOffset,
        searchValue: "",
        limit: itemsPerPage,
        type: searchType,
        startDate: convertDateToParamsFormat(startDate),
        endDate: convertDateToParamsFormat(endDate)
      });
    };
    $[12] = currentPageFirstOffset;
    $[13] = endDate;
    $[14] = searchType;
    $[15] = startDate;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== currentPageFirstOffset || $[18] !== searchType) {
    t6 = [currentPageFirstOffset, searchType];
    $[17] = currentPageFirstOffset;
    $[18] = searchType;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  useEffect(t5, t6);
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      placeholderData: keepPreviousData
    };
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const {
    data: userGiftList
  } = useFetchUserGiftListQuery(searchParams, t7);
  const isListEmpty = !userGiftList?.page.total;
  let t8;
  if ($[21] !== pushModal) {
    t8 = t9 => {
      const {
        giftId,
        giftNick
      } = t9;
      pushModal({
        type: MODAL_TYPE_PC.STORE_MODAL,
        id: "useGiftListViewModel",
        options: {
          giftId,
          giftNick
        }
      });
    };
    $[21] = pushModal;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const showStoreModal = t8;
  let t9;
  if ($[23] !== pushModal) {
    t9 = t10 => {
      const {
        userId,
        userNick,
        userIdx
      } = t10;
      pushModal({
        type: MODAL_TYPE_PC.SEND_MESSAGE_MODAL,
        id: "useGiftListViewModel",
        options: {
          userId,
          userNick,
          userIdx
        }
      });
    };
    $[23] = pushModal;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const showMessageModal = t9;
  let t10;
  if ($[25] !== endDate || $[26] !== searchId || $[27] !== searchType || $[28] !== startDate) {
    t10 = event => {
      event.preventDefault();
      setSearchParams({
        offset: 0,
        limit: itemsPerPage,
        type: searchType,
        startDate: convertDateToParamsFormat(startDate),
        endDate: convertDateToParamsFormat(endDate),
        searchValue: searchId
      });
      setCurrentPage(1);
    };
    $[25] = endDate;
    $[26] = searchId;
    $[27] = searchType;
    $[28] = startDate;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const handleSearchClick = t10;
  let t11;
  if ($[30] !== currentPage || $[31] !== currentPageFirstOffset || $[32] !== endDate || $[33] !== handleSearchClick || $[34] !== isListEmpty || $[35] !== searchId || $[36] !== searchType || $[37] !== showMessageModal || $[38] !== showStoreModal || $[39] !== startDate || $[40] !== userGiftList) {
    t11 = {
      userGiftList,
      searchId,
      startDate,
      endDate,
      currentPage,
      currentPageFirstOffset,
      searchType,
      setSearchType,
      setStartDate,
      setEndDate,
      setSearchId,
      setCurrentPage,
      isListEmpty,
      showStoreModal,
      showMessageModal,
      handleSearchClick
    };
    $[30] = currentPage;
    $[31] = currentPageFirstOffset;
    $[32] = endDate;
    $[33] = handleSearchClick;
    $[34] = isListEmpty;
    $[35] = searchId;
    $[36] = searchType;
    $[37] = showMessageModal;
    $[38] = showStoreModal;
    $[39] = startDate;
    $[40] = userGiftList;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  return t11;
}
function _temp(state) {
  return state.pushModal;
}
export default useGiftListViewModel;